/**
 * Event-page redesign (2026-05-22) — `<PhotoLightbox>` opens a modal
 * over the gallery showing the tapped photo at the larger `reveal`
 * derivative size (~1920px long edge). Mobile-first with prev/next
 * navigation via on-screen arrows, keyboard arrows, and horizontal
 * touch swipe.
 *
 * Dependency-free — uses a plain fixed-position overlay (NOT the native
 * `<dialog>` element, which has spotty mobile Safari support for the
 * styles we need). Backdrop click + Escape key + close button all
 * dismiss. Keyboard focus is trapped to the overlay while open.
 *
 * The lightbox does NOT manage its own photo list; the parent supplies
 * the indexed `photos` + the `index` and updates `index` via
 * `onIndexChange`. This keeps the lightbox stateless w.r.t. data.
 *
 * Tradeoffs surfaced (per user instruction "Surface tradeoffs"):
 *   - Lightbox does NOT support pinch-zoom in v1. The native browser
 *     image viewer (long-press → "Open in new tab" or download) is
 *     reachable via the "Open in larger view" link. Pinch-zoom is a
 *     reasonable v2 with a small library; out of scope here.
 *   - We DON'T preload neighbor photos. The first prev/next will show
 *     a brief flash on slow networks; mitigated by `preload="auto"`
 *     hints in v2.
 *   - Tap-to-toggle-chrome is not implemented (overlay chrome is
 *     always visible).
 *
 * [Source: _bmad-output/planning-artifacts/event-page-redesign-ux.md]
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { HeartButton } from './HeartButton';

export interface PhotoLightboxItem {
  photoId: string;
  submitterName: string;
  derivativeUrls: {
    card: { webp: string; jpg: string };
    reveal?: { webp: string; jpg: string };
  };
}

export interface PhotoLightboxProps {
  /** All photos in the gallery; `index` is bounded against this list. */
  photos: PhotoLightboxItem[];
  /** Current zero-based photo index. `null` closes the lightbox. */
  index: number | null;
  /** Fired when user dismisses the lightbox. */
  onClose: () => void;
  /** Fired with the new index after prev/next/swipe. */
  onIndexChange: (next: number) => void;
  /**
   * Story 21.4 — photoIds the voter has upvoted. When `onUpvote` AND
   * `onUnvote` are both undefined, the lightbox is read-only and the
   * heart is NOT rendered (preserves the pre-21.4 behavior in
   * non-voting-open phases).
   */
  youVotedSet?: Set<string>;
  /** Story 21.4 — fired when the heart is tapped on an idle photo. */
  onUpvote?: (photoId: string) => void;
  /** Story 21.4 — fired when the heart is tapped on a voted photo. */
  onUnvote?: (photoId: string) => void;
  /**
   * Story 21.4 — returns `null` when the heart is interactive for the
   * given photo, OR a reason string (e.g. "Voters can't upvote their
   * own photo.", "Vote cap reached. Unvote a photo to free a vote.",
   * "Voting is closed.") when the heart should render disabled.
   * The reason is surfaced via the `title` attribute and `aria-disabled`.
   * Mirrors the gallery's `disabledPredicate` + tooltip-copy split.
   */
  votingDisabledReason?: (photoId: string) => string | null;
}

const SWIPE_THRESHOLD_PX = 60;

export function PhotoLightbox({
  photos,
  index,
  onClose,
  onIndexChange,
  youVotedSet,
  onUpvote,
  onUnvote,
  votingDisabledReason,
}: PhotoLightboxProps) {
  const isOpen = index !== null && photos.length > 0;
  const safeIndex = useMemo(() => {
    if (index === null) return 0;
    if (index < 0) return 0;
    if (index >= photos.length) return photos.length - 1;
    return index;
  }, [index, photos.length]);
  const photo = isOpen ? photos[safeIndex] : null;

  // Track the touch X so we can compute swipe delta on touchend.
  const touchStartXRef = useRef<number | null>(null);

  const goPrev = useCallback(() => {
    if (!isOpen) return;
    if (safeIndex > 0) onIndexChange(safeIndex - 1);
  }, [isOpen, safeIndex, onIndexChange]);

  const goNext = useCallback(() => {
    if (!isOpen) return;
    if (safeIndex < photos.length - 1) onIndexChange(safeIndex + 1);
  }, [isOpen, safeIndex, photos.length, onIndexChange]);

  // Lock body scroll while open + restore on close.
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  // Keyboard handlers — Escape closes, ArrowLeft/Right navigate.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
        return;
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
        return;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose, goPrev, goNext]);

  // Move focus into the dialog when it opens for screen readers + keyboard.
  const dialogRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!isOpen) return;
    const node = dialogRef.current;
    if (node !== null) {
      // setTimeout to let the dialog render before focusing
      const handle = window.setTimeout(() => {
        try {
          node.focus();
        } catch {
          // ignore
        }
      }, 0);
      return () => window.clearTimeout(handle);
    }
    return;
  }, [isOpen]);

  // Local "open in larger view" state for the link
  const [largerView, setLargerView] = useState(false);
  useEffect(() => {
    setLargerView(false);
  }, [safeIndex]);

  if (!isOpen || photo === null || photo === undefined) return null;

  const reveal = photo.derivativeUrls.reveal;
  const card = photo.derivativeUrls.card;
  // Prefer reveal when present; fall back to card for older photos.
  const displayJpg = reveal?.jpg ?? card.jpg;
  const displayWebp = reveal?.webp ?? card.webp;

  // Story 21.4 — voting controls. Heart only renders when the parent
  // wires at least one of `onUpvote` or `onUnvote`; this preserves the
  // pre-21.4 read-only behavior for non-voting-open phases (parent
  // omits both handlers in those phases).
  const showHeart = onUpvote !== undefined || onUnvote !== undefined;
  const isHeartActive = youVotedSet?.has(photo.photoId) ?? false;
  // `votingDisabledReason` returns null/undefined when interactive, or a
  // reason string when disabled. Mirrors GalleryGrid's title-copy split
  // (self-vote / cap / wrong-phase) — the parent wires the same
  // predicate composition.
  const heartDisabledReason: string | null =
    showHeart && votingDisabledReason !== undefined
      ? (votingDisabledReason(photo.photoId) ?? null)
      : null;

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (e.touches.length === 1) {
      touchStartXRef.current = e.touches[0]?.clientX ?? null;
    }
  };
  const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    const startX = touchStartXRef.current;
    touchStartXRef.current = null;
    if (startX === null) return;
    const endX = e.changedTouches[0]?.clientX ?? startX;
    const delta = endX - startX;
    if (Math.abs(delta) < SWIPE_THRESHOLD_PX) return;
    if (delta > 0) {
      goPrev();
    } else {
      goNext();
    }
  };

  // Inline style: full-screen fixed overlay with z above sticky header.
  const overlayStyle: CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(8, 10, 15, 0.92)',
    zIndex: 100,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'space-between',
    color: 'var(--text-primary)',
  };

  const photoFrameStyle: CSSProperties = {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'var(--space-3)',
    position: 'relative',
  };

  const buttonChromeStyle: CSSProperties = {
    background: 'rgba(255, 255, 255, 0.08)',
    color: '#FFFFFF',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    borderRadius: 'var(--radius-md)',
    padding: 'var(--space-2) var(--space-3)',
    fontSize: 'var(--text-sm-size)',
    fontFamily: 'inherit',
    cursor: 'pointer',
    minHeight: 44,
    minWidth: 44,
  };

  const arrowButtonStyle: CSSProperties = {
    ...buttonChromeStyle,
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    fontSize: 'var(--text-xl-size)',
    fontWeight: 600,
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Photo by ${photo.submitterName}`}
      ref={dialogRef}
      tabIndex={-1}
      data-testid="photo-lightbox"
      style={overlayStyle}
      onClick={(e) => {
        // Click on the backdrop (not on a child) closes.
        if (e.target === e.currentTarget) onClose();
      }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Top chrome — close button + counter. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--space-3)',
          padding: 'var(--space-3)',
          color: '#FFFFFF',
        }}
      >
        <span
          data-testid="photo-lightbox-counter"
          style={{ fontSize: 'var(--text-sm-size)', fontWeight: 500 }}
        >
          {safeIndex + 1} of {photos.length}
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close photo viewer"
          data-testid="photo-lightbox-close"
          style={{ ...buttonChromeStyle, fontSize: 'var(--text-lg-size)' }}
        >
          Close
        </button>
      </div>

      {/* Photo. */}
      <div style={photoFrameStyle}>
        {photos.length > 1 && safeIndex > 0 && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              goPrev();
            }}
            aria-label="Previous photo"
            data-testid="photo-lightbox-prev"
            style={{ ...arrowButtonStyle, left: 'var(--space-3)' }}
          >
            &#8249;
          </button>
        )}
        {/*
         * Story 23.4 — submitter name overlaid on the photo bottom as a
         * gradient strip (mirrors the gallery's pre-23.4 treatment). The
         * gradient sits inside the photo frame and aligns to the bottom
         * edge of the frame; the picture itself sits above it. Because
         * the picture uses `object-fit: contain`, the strip may extend
         * slightly beyond the photo's bottom edge on letterboxed
         * portraits — that's fine because the strip is constrained to
         * the photoFrame's `var(--space-3)` padding box.
         */}
        <picture
          // Stop propagation so the photo itself doesn't dismiss the
          // overlay when tapped.
          onClick={(e) => e.stopPropagation()}
        >
          <source srcSet={displayWebp} type="image/webp" />
          <img
            src={displayJpg}
            alt={`Photo by ${photo.submitterName}`}
            data-testid="photo-lightbox-image"
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              display: 'block',
              borderRadius: 'var(--radius-md)',
            }}
          />
        </picture>
        <div
          data-testid="photo-lightbox-name-overlay"
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute',
            left: 'var(--space-3)',
            right: 'var(--space-3)',
            bottom: 'var(--space-3)',
            padding: 'var(--space-3)',
            background:
              'linear-gradient(to top, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0) 100%)',
            borderBottomLeftRadius: 'var(--radius-md)',
            borderBottomRightRadius: 'var(--radius-md)',
            pointerEvents: 'none',
          }}
        >
          <span
            data-testid="photo-lightbox-submitter"
            style={{
              fontSize: 'var(--text-base-size)',
              fontWeight: 700,
              color: '#FFFFFF',
              textShadow: '0 1px 2px rgba(0,0,0,0.6)',
            }}
          >
            {photo.submitterName}
          </span>
        </div>
        {photos.length > 1 && safeIndex < photos.length - 1 && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              goNext();
            }}
            aria-label="Next photo"
            data-testid="photo-lightbox-next"
            style={{ ...arrowButtonStyle, right: 'var(--space-3)' }}
          >
            &#8250;
          </button>
        )}
      </div>

      {/* Bottom chrome — (Story 21.4) heart + open-larger link. The
          submitter name moved onto the photo as a gradient overlay
          (Story 23.4). */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 'var(--space-3)',
          padding: 'var(--space-3)',
          color: '#FFFFFF',
        }}
      >
        {showHeart && (
          <div
            data-testid="photo-lightbox-heart-wrap"
            // Browser default `title` provides the long-press tooltip on
            // touch + hover tooltip on desktop; mirrors GalleryGrid's
            // wrapping-div tooltip pattern (CSD #3 sub-lock copy).
            title={heartDisabledReason ?? undefined}
            style={{
              // Override HeartButton's color tokens so the icon renders
              // #FFFFFF for both filled (active) and outlined (idle)
              // states over the dark `rgba(8,10,15,0.92)` overlay.
              // Filled-vs-outlined distinction is preserved via the SVG's
              // `fill="currentColor"` vs `fill="none"`. White on
              // rgba(8,10,15,0.92) hits ~17:1 contrast — far above WCAG
              // AA. Mirrors Story 21.2's gallery-overlay treatment.
              ['--upvote-idle' as string]: '#FFFFFF',
              ['--upvote-active' as string]: '#FFFFFF',
            }}
          >
            <HeartButton
              isActive={isHeartActive}
              disabled={heartDisabledReason !== null}
              onActivate={() => {
                if (onUpvote !== undefined) onUpvote(photo.photoId);
              }}
              onDeactivate={() => {
                if (onUnvote !== undefined) onUnvote(photo.photoId);
              }}
            />
          </div>
        )}
        {!largerView ? (
          <button
            type="button"
            onClick={() => setLargerView(true)}
            data-testid="photo-lightbox-larger"
            style={{ ...buttonChromeStyle, textDecoration: 'underline' }}
          >
            Open in larger view
          </button>
        ) : (
          <a
            href={displayJpg}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="photo-lightbox-larger-link"
            style={{
              ...buttonChromeStyle,
              textDecoration: 'underline',
              color: '#FFFFFF',
            }}
          >
            Open photo in new tab
          </a>
        )}
      </div>
    </div>
  );
}
